import React, { useState, useEffect } from 'react';
import AppointmentTimelineTable from '../Body/AppointmentTimelineTable';
import AppointmentService from '../services/appointmentTimelineService';
import DoctorManagementMenu from "../../../components/common/Menu/DoctorManagement";
import TopHeader from "../../../components/common/Header/TopHeader";

const AppointmentTimelineList = () => {
    const [appointments, setAppointments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Instancia del servicio
    const appointmentService = new AppointmentService();

    // Fetch appointments from API when component mounts
    useEffect(() => {
        fetchAppointments();
    }, []);

    // function to fetch appointments from the API
    const fetchAppointments = async () => {
        try {
            setIsLoading(true);
            setError(null);
            
            console.log('🔍 Iniciando fetch de appointments...');
            
            // El servicio ya maneja la llamada HTTP y devuelve los datos procesados
            const responseData = await appointmentService.getAllAppointments({
                // Puedes agregar parámetros de filtrado aquí si necesitas
                // status: 'all', // Para obtener todas las citas independientemente del estado
                // includeDeleted: false, // Para excluir citas eliminadas
                // sortBy: 'appointmentDate', // Para ordenar por fecha
                // sortOrder: 'asc' // Orden ascendente
            });
            
            console.log('📡 Respuesta del servicio:', responseData);

            // Verificar diferentes estructuras de respuesta posibles
            let appointmentsData = [];
            
            if (responseData.status && responseData.data) {
                // Estructura: { status: true, data: [...] }
                appointmentsData = responseData.data;
            } else if (responseData.success && responseData.data) {
                // Estructura: { success: true, data: [...] }
                appointmentsData = responseData.data;
            } else if (Array.isArray(responseData)) {
                // Respuesta directa como array
                appointmentsData = responseData;
            } else if (responseData.appointments) {
                // Estructura: { appointments: [...] }
                appointmentsData = responseData.appointments;
            } else if (responseData.results) {
                // Estructura: { results: [...] }
                appointmentsData = responseData.results;
            } else {
                console.error('❌ Estructura de respuesta no reconocida:', responseData);
                throw new Error('Estructura de respuesta no válida');
            }

            console.log('📊 Datos de appointments encontrados:', appointmentsData);
            console.log('📊 Cantidad de appointments:', appointmentsData.length);

            if (!Array.isArray(appointmentsData)) {
                throw new Error('Los datos recibidos no son un array válido');
            }

            if (appointmentsData.length === 0) {
                console.log('⚠️ No se encontraron citas');
                setAppointments([]);
                return;
            }

            // Process appointments to ensure all required fields are present
            const processedAppointments = appointmentsData.map((appointment, index) => {
                console.log(`📝 Procesando appointment ${index + 1}:`, appointment);
                
                return {
                    ...appointment,
                    // Asegurar que los campos obligatorios existan
                    _id: appointment._id || appointment.id || `temp-${index}`,
                    appointmentId: appointment.appointmentId || `APT-${Date.now()}-${index}`,
                    
                    // Información del doctor
                    doctor: appointment.doctor || { 
                        _id: 'unknown',
                        name: 'Doctor no asignado',
                        firstName: 'No asignado',
                        lastName: '',
                        specialization: 'N/A',
                        phone: 'N/A',
                        email: 'N/A'
                    },
                    
                    // Información del paciente
                    patient: appointment.patient || { 
                        _id: 'unknown',
                        name: 'Paciente no asignado',
                        firstName: 'No asignado',
                        lastName: '',
                        phone: 'N/A',
                        email: 'N/A',
                        dateOfBirth: 'N/A'
                    },
                    
                    // Detalles de la cita
                    appointmentDate: appointment.appointmentDate || new Date().toISOString().split('T')[0],
                    appointmentTime: appointment.appointmentTime || '00:00',
                    duration: appointment.duration || 30, // duración en minutos
                    
                    // Estado y seguimiento
                    status: appointment.status || 'pending',
                    priority: appointment.priority || 'normal',
                    type: appointment.type || 'consultation',
                    
                    // Notas y detalles adicionales
                    notes: appointment.notes || '',
                    reasonForVisit: appointment.reasonForVisit || '',
                    symptoms: appointment.symptoms || '',
                    
                    // Información de consulta
                    consultationStarted: appointment.consultationStarted || false,
                    consultationCompleted: appointment.consultationCompleted || false,
                    checkedIn: appointment.checkedIn || false,
                    checkedInAt: appointment.checkedInAt || null,
                    
                    // Timestamps
                    createdAt: appointment.createdAt || new Date().toISOString(),
                    updatedAt: appointment.updatedAt || new Date().toISOString(),
                    
                    // Información adicional que puede ser útil para la tabla
                    estimatedWaitTime: appointment.estimatedWaitTime || 0,
                    roomNumber: appointment.roomNumber || 'TBD',
                    isUrgent: appointment.isUrgent || false,
                    followUp: appointment.followUp || false,
                    
                    // Métodos de pago y seguro (si aplica)
                    insurance: appointment.insurance || null,
                    copay: appointment.copay || 0,
                    
                    // Información de cancelación
                    cancelled: appointment.cancelled || false,
                    cancellationReason: appointment.cancellationReason || '',
                    cancelledAt: appointment.cancelledAt || null
                };
            });
            
            console.log('✅ Appointments procesados:', processedAppointments);
            setAppointments(processedAppointments);
            
        } catch (error) {
            console.error('❌ Error fetching appointments:', error);
            console.error('❌ Error stack:', error.stack);
            
            // Proporcionar más información sobre el error
            let errorMessage = 'Error al cargar las citas. ';
            
            if (error.name === 'TypeError' && error.message.includes('fetch')) {
                errorMessage += 'No se pudo conectar con el servidor. Verifique su conexión.';
            } else if (error.message.includes('JSON')) {
                errorMessage += 'Respuesta del servidor no válida (formato JSON incorrecto).';
            } else if (error.message.includes('404')) {
                errorMessage += 'Endpoint no encontrado. Verifique la URL del API.';
            } else if (error.message.includes('500')) {
                errorMessage += 'Error interno del servidor. Contacte al administrador.';
            } else if (error.message.includes('401')) {
                errorMessage += 'No autorizado. Verifique sus credenciales.';
            } else if (error.message.includes('403')) {
                errorMessage += 'Acceso denegado. No tiene permisos suficientes.';
            } else {
                errorMessage += error.message || 'Error desconocido.';
            }
            
            setError(errorMessage);
            setAppointments([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Funciones adicionales para operaciones específicas
    const refreshAppointments = () => {
        fetchAppointments();
    };

    // Función para obtener citas por doctor específico (si necesitas filtrar)
    const fetchDoctorAppointments = async (doctorId) => {
        try {
            setIsLoading(true);
            setError(null);
            
            // El servicio ya maneja la llamada HTTP
            const responseData = await appointmentService.getDoctorAppointments(doctorId);
            
            let appointmentsData = [];
            if (responseData.data) {
                appointmentsData = responseData.data;
            } else if (Array.isArray(responseData)) {
                appointmentsData = responseData;
            }
            
            // Procesar datos similar a fetchAppointments
            const processedAppointments = appointmentsData.map((appointment, index) => ({
                ...appointment,
                _id: appointment._id || appointment.id || `temp-${index}`,
                appointmentId: appointment.appointmentId || `APT-${Date.now()}-${index}`,
                // ... resto del procesamiento igual que en fetchAppointments
            }));
            
            setAppointments(processedAppointments);
        } catch (error) {
            console.error('❌ Error fetching doctor appointments:', error);
            setError('Error al cargar citas del doctor: ' + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    // Función para obtener citas por paciente específico (si necesitas filtrar)
    const fetchPatientAppointments = async (patientId) => {
        try {
            setIsLoading(true);
            setError(null);
            
            // El servicio ya maneja la llamada HTTP
            const responseData = await appointmentService.getPatientAppointments(patientId);
            
            let appointmentsData = [];
            if (responseData.data) {
                appointmentsData = responseData.data;
            } else if (Array.isArray(responseData)) {
                appointmentsData = responseData;
            }
            
            // Procesar datos similar a fetchAppointments
            const processedAppointments = appointmentsData.map((appointment, index) => ({
                ...appointment,
                _id: appointment._id || appointment.id || `temp-${index}`,
                appointmentId: appointment.appointmentId || `APT-${Date.now()}-${index}`,
                // ... resto del procesamiento igual que en fetchAppointments
            }));
            
            setAppointments(processedAppointments);
        } catch (error) {
            console.error('❌ Error fetching patient appointments:', error);
            setError('Error al cargar citas del paciente: ' + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    // Función para actualizar el estado de una cita
    const updateAppointmentStatus = async (appointmentId, newStatus, notes = null) => {
        try {
            console.log('🔄 Actualizando estado de cita:', { appointmentId, newStatus, notes });
            
            let updatedAppointment;
            if (notes) {
                updatedAppointment = await appointmentService.updateAppointmentStatusWithNotes(appointmentId, newStatus, notes);
            } else {
                updatedAppointment = await appointmentService.updateAppointmentStatus(appointmentId, newStatus);
            }
            
            // Actualizar el estado local
            setAppointments(prevAppointments => 
                prevAppointments.map(appointment => 
                    appointment._id === appointmentId 
                        ? { ...appointment, ...updatedAppointment.data || updatedAppointment }
                        : appointment
                )
            );
            
            console.log('✅ Estado actualizado correctamente');
            return updatedAppointment;
        } catch (error) {
            console.error('❌ Error updating appointment status:', error);
            setError('Error al actualizar el estado de la cita: ' + error.message);
            throw error;
        }
    };

    // Función para check-in de paciente
    const checkinPatient = async (appointmentId) => {
        try {
            console.log('✅ Realizando check-in para cita:', appointmentId);
            
            const updatedAppointment = await appointmentService.checkinPatient(appointmentId);
            
            // Actualizar el estado local
            setAppointments(prevAppointments => 
                prevAppointments.map(appointment => 
                    appointment._id === appointmentId 
                        ? { ...appointment, ...updatedAppointment.data || updatedAppointment }
                        : appointment
                )
            );
            
            console.log('✅ Check-in realizado correctamente');
            return updatedAppointment;
        } catch (error) {
            console.error('❌ Error during check-in:', error);
            setError('Error al realizar check-in: ' + error.message);
            throw error;
        }
    };

    // Función para iniciar consulta
    const startConsultation = async (appointmentId) => {
        try {
            console.log('🏥 Iniciando consulta para cita:', appointmentId);
            
            const updatedAppointment = await appointmentService.startConsultation(appointmentId);
            
            // Actualizar el estado local
            setAppointments(prevAppointments => 
                prevAppointments.map(appointment => 
                    appointment._id === appointmentId 
                        ? { ...appointment, ...updatedAppointment.data || updatedAppointment }
                        : appointment
                )
            );
            
            console.log('✅ Consulta iniciada correctamente');
            return updatedAppointment;
        } catch (error) {
            console.error('❌ Error starting consultation:', error);
            setError('Error al iniciar consulta: ' + error.message);
            throw error;
        }
    };

    // Función para completar consulta
    const completeConsultation = async (appointmentId, notes = null) => {
        try {
            console.log('🏁 Completando consulta para cita:', appointmentId, 'con notas:', notes);
            
            const updatedAppointment = await appointmentService.completeConsultation(appointmentId, notes);
            
            // Actualizar el estado local
            setAppointments(prevAppointments => 
                prevAppointments.map(appointment => 
                    appointment._id === appointmentId 
                        ? { ...appointment, ...updatedAppointment.data || updatedAppointment }
                        : appointment
                )
            );
            
            console.log('✅ Consulta completada correctamente');
            return updatedAppointment;
        } catch (error) {
            console.error('❌ Error completing consultation:', error);
            setError('Error al completar consulta: ' + error.message);
            throw error;
        }
    };

    // Función para debug - puedes llamarla desde las herramientas del desarrollador
    window.debugAppointments = () => {
        console.log('🔍 DEBUG INFO:');
        console.log('📊 Estado actual:', {
            appointments: appointments,
            isLoading: isLoading,
            error: error,
            appointmentsLength: appointments.length
        });
        console.log('🔧 Servicio utilizado:', appointmentService);
        console.log('🔧 Reintentando fetch...');
        fetchAppointments();
    };

    // Función para limpiar errores
    const clearError = () => {
        setError(null);
    };
        
    return (
        <main id="app">
            <TopHeader />
            <div className="content_wrapper">
            <div class="doctor-management-wrapper bg-white pb-5">
            
                <DoctorManagementMenu activeMenu='/appointments'/>
                
                        <section>
                            <AppointmentTimelineTable
                                appointments={appointments}
                                onRefresh={refreshAppointments}
                                onUpdateStatus={updateAppointmentStatus}
                                onCheckin={checkinPatient}
                                onStartConsultation={startConsultation}
                                onCompleteConsultation={completeConsultation}
                                isLoading={isLoading}
                            />                                
                        </section>
                </div>
                
            </div>
        </main>
    );
};

export default AppointmentTimelineList;